/**
 * shortcodes.service.ts — the ${...} shortcodes a document body can carry
 * (GET /v5/surveys/shortcodes): agreements, forms and the like fill them in
 * for the patient when they are shown. The API groups them by area
 * (general, patient_demographics, plan, appointment…); each is a code and a
 * description.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface ShortcodeGroup {
  /** "Patient Demographics", from the API's patient_demographics key. */
  category: string;
  items: { code: string; description: string }[];
}

function titleCase(key: string): string {
  return key
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function listShortcodes(): Promise<ShortcodeGroup[]> {
  return apiClient.get<ApiResponse<Record<string, { code?: string; description?: string }[]>>>('/surveys/shortcodes').then((r) => {
    const data = r.data?.data;
    if (!data || typeof data !== 'object') return [];
    return Object.entries(data)
      .map(([key, items]) => ({
        category: titleCase(key),
        items: (Array.isArray(items) ? items : [])
          .map((i) => ({ code: String(i.code ?? ''), description: String(i.description ?? '') }))
          .filter((i) => i.code),
      }))
      .filter((g) => g.items.length > 0);
  });
}
